import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import {
    PudoTransactionTimersDurationByUserDropOffListChart,
    PudoTransactionTimersDurationByUserPickUpListChart,
    takeRankedChartSeries,
    toChartListItems
} from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { useEffect, useState } from 'react';

import { getLast30DaysChartRange } from '../../../statistics/pudo/last-30-days-chart-range';
import { useInsightDetailMode } from '../insight-detail-mode.context';
import { useInsightFetchRange } from '../insight-detail/use-insight-fetch-range';

export type PudoTransactionTimerDurationUserKind = 'drop-off' | 'pick-up';

/** Ranked preview size on the insights overview cards. */
const OVERVIEW_USER_LIMIT = 10;

export interface PudoTransactionTimerDurationByUserItem {
    label: string;
    /** Average completed pending-timer duration in seconds. */
    durationSeconds: number;
}

const isDisplayableLabel = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 && normalized !== 'none' && normalized !== 'default';
};

const toUserItems = (chart: Chart): PudoTransactionTimerDurationByUserItem[] =>
    toChartListItems(chart, '')
        .filter((item) => isDisplayableLabel(item.name) && item.value > 0)
        .map((item) => ({
            label: item.name,
            durationSeconds: item.value
        }));

export const usePudoTransactionTimerDurationByUserList = (accountId: string, timerKind: PudoTransactionTimerDurationUserKind) => {
    const isDetailMode = useInsightDetailMode();
    const [items, setItems] = useState<PudoTransactionTimerDurationByUserItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        const definition =
            timerKind === 'drop-off'
                ? PudoTransactionTimersDurationByUserDropOffListChart.definition
                : PudoTransactionTimersDurationByUserPickUpListChart.definition;

        ChartClient.fetchChartData(accountId, definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams,
            [DefaultQueryParams.SORTDIRECTION]: 'desc'
        })
            .then((chart) => {
                if (cancelled) return;
                const ranked = isDetailMode
                    ? takeRankedChartSeries(chart, 'highest', Number.POSITIVE_INFINITY)
                    : takeRankedChartSeries(chart, 'highest', OVERVIEW_USER_LIMIT);
                setItems(toUserItems(ranked));
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, timerKind, fetchRange.key, isDetailMode]);

    return { items, isLoading, hasData: items.length > 0, isDetailMode };
};
